type DiagnosticLevel = 'debug' | 'info' | 'warn' | 'error'

type DiagnosticGlobal = typeof globalThis & {
  readonly localStorage?: {
    readonly getItem: (key: string) => string | null
  }
  readonly process?: {
    readonly env?: Record<string, string | undefined>
  }
}

type DiagnosticImportMeta = ImportMeta & {
  readonly env?: {
    readonly DEV?: boolean
    readonly VITE_ASCLEO_FRONTDESK_DIAGNOSTICS?: string
  }
}

const DIAGNOSTIC_STORAGE_KEY = 'ascleo.frontdesk.diagnostics'
const SENSITIVE_KEY_PATTERN =
  /authorization|bearer|cookie|dob|dateOfBirth|deviceName|egk|email|iknr|kvnr|name|patient|password|phone|secret|session|token/i
const SENSITIVE_MESSAGE_PATTERN =
  /\b(authorization|bearer|cookie|dob|dateOfBirth|deviceName|egk|email|iknr|kvnr|name|patient|password|phone|secret|session|token|id|appointment|device|terminal)\b\s*[:=]?\s*[^,\s)]+/gi

function enumerableStringEntries(value: object): Array<readonly [string, unknown]> {
  const entries: Array<readonly [string, unknown]> = []
  for (const key of Object.keys(value)) {
    const entry: unknown = Reflect.get(value, key)
    entries.push([key, entry])
  }
  return entries
}

const getDiagnosticEnv = () => {
  const runtime = globalThis as DiagnosticGlobal
  const viteEnv = (import.meta as DiagnosticImportMeta).env
  const processEnv = runtime.process?.env

  return {
    isDev: viteEnv?.DEV === true || processEnv?.NODE_ENV === 'development',
    enabled:
      viteEnv?.VITE_ASCLEO_FRONTDESK_DIAGNOSTICS === 'true' ||
      processEnv?.VITE_ASCLEO_FRONTDESK_DIAGNOSTICS === 'true' ||
      processEnv?.ASCLEO_FRONTDESK_DIAGNOSTICS === 'true',
    storageEnabled: runtime.localStorage?.getItem(DIAGNOSTIC_STORAGE_KEY) === 'true'
  }
}

const isEnabled = () => {
  const env = getDiagnosticEnv()
  return env.enabled || (env.isDev && env.storageEnabled)
}

const sanitizeValue = (value: unknown): unknown => {
  if (value instanceof Error) {
    return {
      errorType: value.name || 'Error',
      hasMessage: Boolean(value.message)
    }
  }

  if (Array.isArray(value)) {
    return {
      itemCount: value.length
    }
  }

  if (typeof value === 'string') {
    return value.length > 160
      ? {
          hasValue: true,
          length: value.length
        }
      : value
  }

  if (!value || typeof value !== 'object') {
    return value
  }

  return Object.fromEntries(
    enumerableStringEntries(value).map(([key, entry]) => [
      key,
      SENSITIVE_KEY_PATTERN.test(key) ? '[redacted]' : sanitizeValue(entry)
    ])
  )
}

const sanitizeMessage = (message: string) =>
  message
    .replace(/\b[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\b/gi, '[id]')
    .replace(/https?:\/\/\S+/gi, '[url]')
    .replace(/\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi, '[email]')
    .replace(SENSITIVE_MESSAGE_PATTERN, (_match, label: string) => `${label} [redacted]`)

const emit = (level: DiagnosticLevel, message: string, ...values: readonly unknown[]) => {
  if (!isEnabled()) {
    return
  }

  const sanitized = values.map(sanitizeValue)
  globalThis.console[level](sanitizeMessage(message), ...sanitized)
}

export const frontdeskDiagnosticLogger = {
  debug: (message: string, ...values: readonly unknown[]) => emit('debug', message, ...values),
  info: (message: string, ...values: readonly unknown[]) => emit('info', message, ...values),
  warn: (message: string, ...values: readonly unknown[]) => emit('warn', message, ...values),
  error: (message: string, ...values: readonly unknown[]) => emit('error', message, ...values)
} as const
